import { DynamicForm, FieldVariant, CustomFieldProps } from '@/components/form-builder/form-builder.types';
import { DemandeWithTaskDTO } from '@/types';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { OutputQuestionsReadonlyComponent } from '../output-questions-readonly/output-questions-readonly.component';
import { OutputSignedDocComponent } from '../output-signed-doc/output-signed-doc.component';
import { ClarificationRepository } from '@/store/clarification-request-state/clarification-request-state.repository';

export const getOutputDetailsFormObject = (
  store: ClarificationRepository,
  demandeWithTask: DemandeWithTaskDTO
): DynamicForm => {
  // Always seed the store with the latest data from the API so all
  // reactive observables in the form start with the correct values,
  // regardless of which component builds this form.
  store.set('downloadModeldemandeEclDto', {
    demandeId: demandeWithTask.demande.id,
    haveReport: demandeWithTask.demande.haveReport || false,
    dateReport: demandeWithTask.demande.dateReport || null,
  });

  const haveReport$ = store.getNestedObservable<boolean>('downloadModeldemandeEclDto', 'haveReport');
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const isEditable: boolean = ((demandeWithTask.demande.status === 'BROUILLON') || (demandeWithTask.isUpdatable === true));
  const isHidden: boolean = (((!isEditable) && (!demandeWithTask.demande.haveReport))||(demandeWithTask.demande.typeDemande == "SIMPLE"));
  // Only enforce minDate when there is no pre-existing date (i.e. new input)
  const minDateReport = demandeWithTask.demande.dateReport ? undefined : today;

  const grids: any[] = [
    {
      title: '',
      isHeaderHidden: true,
      gridItems: [
        {
          fields: [
            {
              variant: FieldVariant.TEXT,
              label: 'clarification.table.ref',
              isDisabled: of(true),
              props: {
                value: of(demandeWithTask.demande.reference),
              },
            },
            {
              variant: FieldVariant.TEXT,
              label: 'clarification.table.type',
              isDisabled: of(true),
              props: {
                value: of(demandeWithTask.demande.typeDemande),
              },
            },
          ],

        },
        {
          fields: [
            {
              variant: FieldVariant.SWITCH,
              label: 'clarification.table.haveReport',
              isDisabled: of(!isEditable),
              isHidden: of(isHidden),

              props: {
                checked: haveReport$,
                onChange: (event: any) => {
                  const checked = event && (event.checked !== undefined ? event.checked : event);
                  const currentDto = store.get<any>('downloadModeldemandeEclDto') || {};
                  store.set('downloadModeldemandeEclDto', {
                    ...currentDto,
                    haveReport: !!checked
                  });
                  demandeWithTask.demande.haveReport = !!checked;
                  if (!checked) {
                    store.clearError('dateReport');
                  }
                }
              },
            },

            {
              variant: FieldVariant.DATE,
              label: 'clarification.table.dateReport',
              get isRequired() {
                return !!store.get<any>('downloadModeldemandeEclDto')?.haveReport;
              },

              isDisabled: of(!isEditable),
              isHidden: isHidden
                ? of(true)
                : haveReport$.pipe(
                  map(val => !val || demandeWithTask.demande.typeDemande === 'SIMPLE')
                ),
              error: store.getNestedObservableError('dateReport'),
              props: {
                minDate: minDateReport,
                showTime: true,
                value: store.getNestedObservable<any>('downloadModeldemandeEclDto', 'dateReport').pipe(
                  map(val => val ? new Date(val) : null)
                ),
                onChange: (val: any) => {
                  const currentDto = store.get<any>('downloadModeldemandeEclDto') || {};
                  store.set('downloadModeldemandeEclDto', {
                    ...currentDto,
                    dateReport: val
                  });
                  demandeWithTask.demande.dateReport = val ? new Date(val).toISOString() : null as any;
                  store.clearError('dateReport');
                }
              },
            },
          ],
        }
      ],
    },
  ];

  if (demandeWithTask.demande.signedDoc) {
    grids.push({
      title: 'clarification.form.signed_doc',
      isHeaderHidden: false,
      gridItems: [
        {
          fields: [
            {
              variant: FieldVariant.CUSTOM,
              label: '',
              isRequired: false,
              props: {
                component: OutputSignedDocComponent,
                signedDoc: demandeWithTask.demande.signedDoc,
              } as CustomFieldProps,
            },
          ],
        },
      ],
    });
  }

  grids.push({
    title: 'clarification.form.questions',
    isHeaderHidden: false,
    gridItems: [
      {
        fields: [
          {
            variant: FieldVariant.CUSTOM,
            label: '',
            isRequired: false,
            props: {
              component: OutputQuestionsReadonlyComponent,
              questions: demandeWithTask.demande.questions,
            } as CustomFieldProps,
          },
        ],
      },
    ],
  });

  return {
    title: 'clarification.output_details',
    description: 'clarification.output_details_description',
    isHeaderHidden: true,
    grids: grids,
  };
};
